import React, { useLayoutEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import CalculateIcon from '@mui/icons-material/Calculate';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { getDayJs } from '~/services/TimeService';
import { customerPortalHost } from '~/services/common';

import { BookedPredictedSummary, costTitle } from './BookedPredicted';
import ConfidenceBadge from './ConfidenceBadge';
import ConfidenceSelect from './ConfidenceSelect';
import DepartureActionChip from './DepartureActionChip';
import DeparturePackages from './DeparturePackages';
import DeparturePrice from './DeparturePrice';
import DepartureStatus from './DepartureStatus';
import EmptyPrediction from './EmptyPrediction';
import MarginBandChip from './MarginBandChip';
import PeriodCharts from './PeriodCharts';
import ReviewControls from './ReviewControls';
import ReviewHelp from './ReviewHelp';
import ReviewPanel from './ReviewPanel';
import ReviewStatusPill from './ReviewStatusPill';
import SectionHeading from './SectionHeading';
import { RecommendationPending, TccPricingRecommendation } from './TccPricingProvider';
import TourPackages from './TourPackages';
import { breakEvenText, breakEvenUnavailableTooltip } from './breakEvenText';
import { departureActionDefinitions, departureActionOrder } from './departureActions';
import { emptyPredictionText } from './emptyPredictionText';
import { engagementChange, engagementDelayed } from './engagement';
import {
  PeriodPoint,
  agg,
  basisValue,
  departureOnTrack,
  departureSeries,
  flagLabels,
  money,
  pct,
  periods,
  tourRows,
} from './logic';
import { marginBandRanges } from './marginBands';
import {
  bundleTravellerNote,
  cancellationMoneyCaption,
  cancellationMoneyNote,
  insuranceBreakageTooltip,
} from './moneyNotes';
import { actionColour, actionText, tcc, tccVariables } from './palette';
import { dataAsOf, daysAtStatus, rmStatus } from './reviewLogic';
import { resetPageScroll } from './scroll';
import { tourLinks } from './tourLinks';
import { normalizeToursFilterChanges, toursPageView, validateToursFilters } from './toursPageAdapter';
import { writeDrillFilters } from './toursUrlState';
import { CommandDeparture, CommandFlag, CostBasis, MarginBand } from './types';

const keyMarginBands: Array<MarginBand> = [
  'loss',
  'very_thin',
  'thin',
  'below_target',
  'on_track',
  'healthy',
  'no_sale',
];

const count = (value: number | null | undefined) => (value == null ? '—' : Math.round(value).toLocaleString('en-AU'));
const costTooltip = (departure: CommandDeparture, basis: CostBasis, predicted: boolean) => {
  const totals = agg([departure], basis);
  return predicted ? costTitle(totals.psupplier, totals.pcost) : costTitle(totals.supplier, totals.cost);
};

export default function TourDrillDown({
  rows,
  linkRows,
  tourId,
  basis,
  range,
  back,
  sample = false,
  snapshotDate,
  generatedAt,
  contextFilters,
  optionsComplete = false,
}: {
  rows: Array<CommandDeparture>;
  linkRows: Array<CommandDeparture>;
  tourId: string;
  basis: CostBasis;
  range: { from: string; to: string };
  back: () => void;
  sample?: boolean;
  snapshotDate?: string;
  generatedAt?: string | null;
  contextFilters?: import('./logic').CommandFilters;
  optionsComplete?: boolean;
}) {
  const pageRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const tenant = useSelector<App.State, App.Tenant>((state) => state.tenant);
  const [atTop, setAtTop] = useState(false);
  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    setAtTop(false);
    resetPageScroll(pageRef.current);
    let frame: number;
    const reveal = () => {
      if (resetPageScroll(pageRef.current)) {
        setAtTop(true);
      } else {
        frame = requestAnimationFrame(reveal);
      }
    };
    // Confirm synchronously before paint; only retry on a later frame if a scroll owner resists the reset.
    reveal();
    return () => {
      cancelAnimationFrame(frame);
      window.history.scrollRestoration = previous;
    };
  }, [tourId]);
  const [flag, setFlag] = useState<CommandFlag | null>(null);
  const [month, setMonth] = useState<PeriodPoint | null>(null);
  const drillFilters = {
    dateFrom: contextFilters?.dateFrom ?? range.from,
    dateTo: contextFilters?.dateTo ?? range.to,
    confidenceTier: contextFilters?.confidenceTier ?? [],
    basis,
    inReview: contextFilters?.inReview,
    reviewStatus: contextFilters?.reviewStatus ?? 'all',
    reviewSort: contextFilters?.reviewSort ?? 'priority',
    sellOut: contextFilters?.sellOut ?? false,
  };
  const setDrillFilter = (changes: Partial<typeof drillFilters>) => {
    navigate(
      { pathname: location.pathname, search: writeDrillFilters(location.search, { ...drillFilters, ...changes }) },
      { replace: true },
    );
  };
  const scoped = month ? rows.filter((d) => d.dep >= month.from && d.dep <= month.to) : rows;
  const a = agg(scoped, basis);
  const tour = tourRows(rows, rows, basis, [])[0];
  const links = tourLinks(linkRows, customerPortalHost(tenant));
  const salesforceHost = window.configs.SALESFORCE_HOST?.replace(/\/+$/, '');
  const opportunityId = linkRows.find((departure) => departure.opp_id)?.opp_id;
  const salesforceUrl =
    salesforceHost && opportunityId ? `${salesforceHost}/lightning/r/Opportunity/${opportunityId}/view` : null;
  const engagementDate = rows.find((d) => d.engagement_snapshot_date_syd)?.engagement_snapshot_date_syd;
  const events = rows.find(
    (d) => d.offer_page_views_28d != null || d.tour_add_to_cart_28d != null || d.tour_checkout_views_28d != null,
  );
  const visible = scoped.filter(
    (d) => !flag || (flag === 'green' ? departureOnTrack(d, basis) : basisValue(d, 'flag', basis) === flag),
  );
  if (drillFilters.reviewSort === 'longest') {
    visible.sort(
      (a, b) => (daysAtStatus(b, basis, snapshotDate ?? '') ?? 0) - (daysAtStatus(a, basis, snapshotDate ?? '') ?? 0),
    );
  }
  const monthOptions = periods(rows, basis, 'month', range);
  const pageView = toursPageView({
    ready: true,
    sample,
    snapshotDate: snapshotDate ?? '',
    filters: {
      windowDays: 183,
      region: tour?.region ?? 'All',
      onlyAttention: false,
      ...contextFilters,
      tourId,
      basis,
    },
    attentionFlag: null,
    rows: visible,
    data: rows,
    summary: a,
    regions: [...new Set(rows.map((row) => row.region))],
    range,
  });
  const drillView = {
    ...pageView,
    title: tour?.name ?? 'Tour departures',
    page: 'departure-drilldown',
    engagement: {
      scope: 'All departures for this tour, independent of selected month and action',
      period: 'Last 28 days versus previous 28 days',
      snapshotDate: engagementDate ?? null,
      offerPageViews: {
        current: events?.offer_page_views_28d ?? null,
        previous: events?.offer_page_views_prev_28d ?? null,
      },
      addToCart: { current: events?.tour_add_to_cart_28d ?? null, previous: events?.tour_add_to_cart_prev_28d ?? null },
      checkoutPageViews: {
        current: events?.tour_checkout_views_28d ?? null,
        previous: events?.tour_checkout_views_prev_28d ?? null,
      },
    },
    filters: { ...pageView.filters, action: flag ?? 'all', month: month?.key ?? null },
    filterSchema: {
      dateFrom: { type: 'date' as const },
      dateTo: { type: 'date' as const },
      confidenceTier: pageView.filterSchema.confidenceTier,
      basis: { type: 'enum' as const, values: ['ss', 'rep'] },
      inReview: pageView.filterSchema.inReview,
      reviewStatus: pageView.filterSchema.reviewStatus,
      reviewSort: pageView.filterSchema.reviewSort,
      sellOut: pageView.filterSchema.sellOut,
      action: { type: 'enum' as const, values: ['all', ...departureActionOrder] },
      month: { type: 'enum' as const, values: [null, ...monthOptions.map((point) => point.key)] },
    },
    monthOptions: monthOptions.map((point) => ({
      value: point.key,
      label: point.label,
      from: point.from,
      to: point.to,
    })),
    summaryDepartureCount: scoped.length,
    definitions: {
      ...pageView.definitions,
      scope:
        'Summary follows the selected month. visibleDepartures and departureCount additionally follow the selected action. Dates, confidence and cost basis can be edited here without changing the overview. month:null and action:all clear the local selections.',
    },
  };
  usePageBridge({
    pageId: 'tours-command-centre',
    view: drillView,
    normalizeFilters: normalizeToursFilterChanges,
    edit: (changes) => {
      const next = validateToursFilters(changes, drillView.filterSchema, pageView.filters);
      const local: Partial<typeof drillFilters> = {};
      for (const key of [
        'dateFrom',
        'dateTo',
        'confidenceTier',
        'basis',
        'inReview',
        'reviewStatus',
        'reviewSort',
        'sellOut',
      ] as const) {
        if (key in next) {
          Object.assign(local, { [key]: next[key] });
        }
      }
      if (Object.keys(local).length) {
        setDrillFilter(local);
      }
      if ('action' in changes) {
        setFlag(changes.action === 'all' ? null : (changes.action as CommandFlag));
      }
      if ('month' in changes) {
        setMonth(monthOptions.find((point) => point.key === changes.month) ?? null);
      }
    },
  });
  return (
    <Container
      ref={pageRef}
      data-drill-ready={atTop}
      maxWidth={false}
      sx={(theme) => ({
        opacity: atTop ? 1 : 0,
        '& > :not(button):not(h6):not([data-level-heading])': {
          animation: atTop ? 'tccDepartureReveal 150ms ease-out' : 'none',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
        '@keyframes tccDepartureReveal': { from: { opacity: 0 }, to: { opacity: 1 } },
        overflowAnchor: 'none',
        py: 2,
        pb: 6,
        '& .MuiButton-root, & .MuiToggleButton-root': { textTransform: 'none' },
        ...tccVariables(theme.palette.mode),
      })}
    >
      <Typography variant="caption" color="text.secondary">
        {dataAsOf(generatedAt)}
      </Typography>
      <ReviewControls
        filters={{ windowDays: 183, region: 'All', tourId, onlyAttention: false, ...drillFilters }}
        onChange={setDrillFilter}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" useFlexGap spacing={1} flexWrap="wrap">
          <Button onClick={back} variant="outlined" size="medium" sx={{ borderRadius: 10, px: 2.5 }}>
            ← Back to All Tours
          </Button>
          {links.admin && (
            <Button
              component={Link}
              to={links.admin}
              variant="text"
              size="small"
              color="primary"
              aria-label="Open tour details in Admin"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              sx={{
                fontWeight: 500,
                bgcolor: 'transparent',
                minWidth: 'auto',
                px: 1,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Open in Admin
            </Button>
          )}
          {links.site && (
            <Button
              component="a"
              href={links.site}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              color="primary"
              aria-label="View tour on Luxury Escapes (opens in a new tab)"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              sx={{
                fontWeight: 500,
                bgcolor: 'transparent',
                minWidth: 'auto',
                px: 1,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              View on Luxury Escapes
            </Button>
          )}
          {salesforceUrl && (
            <Button
              component="a"
              href={salesforceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              color="primary"
              aria-label="Open this tour in Salesforce (opens in a new tab)"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              sx={{
                fontWeight: 500,
                bgcolor: 'transparent',
                minWidth: 'auto',
                px: 1,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Open in Salesforce
            </Button>
          )}
        </Stack>
        <PageChatButton />
      </Box>
      <Typography variant="h6">Departure Drill-Down Dashboard</Typography>
      <SectionHeading
        title={`Tour Level: ${tour?.name || tourId}`}
        subtitle="Summary, tour engagement and performance by departure date."
      />
      <Typography color="text.secondary">
        {tour?.region} · {tour?.country || '—'}
      </Typography>
      {!tour && <Alert severity="info">No departures for this tour in the current date window and filters.</Alert>}
      <Card variant="outlined" sx={{ p: 2, my: 2 }} data-testid="drill-filters">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Departures From"
              value={getDayJs(drillFilters.dateFrom || range.from)}
              onChange={(value) => setDrillFilter({ dateFrom: value?.isValid() ? value.format('YYYY-MM-DD') : null })}
              format="DD/MM/YYYY"
              slots={{ openPickerIcon: CalendarMonthIcon }}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label="Departures To"
              value={getDayJs(drillFilters.dateTo || range.to)}
              minDate={getDayJs(drillFilters.dateFrom || range.from)}
              onChange={(value) => setDrillFilter({ dateTo: value?.isValid() ? value.format('YYYY-MM-DD') : null })}
              format="DD/MM/YYYY"
              slots={{ openPickerIcon: CalendarMonthIcon }}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
          <ConfidenceSelect
            value={drillFilters.confidenceTier}
            available={[
              ...new Set(
                linkRows
                  .filter(
                    (d) =>
                      d.dep >= (drillFilters.dateFrom || range.from) &&
                      d.dep <= (drillFilters.dateTo || range.to) &&
                      (!contextFilters?.region ||
                        contextFilters.region === 'All' ||
                        d.region === contextFilters.region) &&
                      (!month || (d.dep >= month.from && d.dep <= month.to)) &&
                      (!flag ||
                        (flag === 'green' ? departureOnTrack(d, basis) : basisValue(d, 'flag', basis) === flag)),
                  )
                  .map((d) => d.pred_confidence_tier || 'indicative'),
              ),
            ]}
            complete={optionsComplete}
            onChange={(confidenceTier) => setDrillFilter({ confidenceTier })}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Cost Basis</InputLabel>
            <Select
              label="Cost Basis"
              value={basis}
              onChange={(event) => setDrillFilter({ basis: event.target.value })}
              startAdornment={<CalculateIcon fontSize="small" />}
            >
              <MenuItem value="ss">Supplier Contract Cost (Sliding Scale)</MenuItem>
              <MenuItem value="rep">Finance Reported Cost (WBR)</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Card>
      <Box
        data-testid="command-centre-summary"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5, my: 2 }}
      >
        <BookedPredictedSummary a={a} />
      </Box>
      <TourPackages departures={scoped} basis={basis} />
      <Card variant="outlined" sx={{ p: 2, my: 2 }} data-testid="tour-engagement">
        <Typography
          variant="subtitle2"
          sx={{ bgcolor: 'action.hover', borderLeft: '4px solid', borderColor: 'divider', p: 1, fontWeight: 700 }}
        >
          Tour-Level Online Interactions (Last 28 Days, All Departures)
        </Typography>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2, my: 1 }}
        >
          {[
            ['Offer Page Views', events?.offer_page_views_28d, events?.offer_page_views_prev_28d],
            ['Add to Cart', events?.tour_add_to_cart_28d, events?.tour_add_to_cart_prev_28d],
            ['Checkout Page Views', events?.tour_checkout_views_28d, events?.tour_checkout_views_prev_28d],
          ].map(([label, current, previous]) => (
            <Box key={String(label)}>
              <Typography variant="body2">{label}</Typography>
              <Typography variant="h4">{count(current as number)}</Typography>
              <Typography variant="caption">{engagementChange(current as number, previous as number)}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" display="block">
          Data as of {engagementDate ? getDayJs(engagementDate).format('D MMM YYYY') : 'Unavailable'}
        </Typography>
        {engagementDate &&
          engagementDelayed(
            sample,
            getDayJs().tz('Australia/Sydney').startOf('day').diff(getDayJs(engagementDate), 'day'),
          ) && (
            <Typography variant="caption" color="text.secondary">
              Data delayed
            </Typography>
          )}
      </Card>
      {month && <Chip label={`Departures: ${month.label}`} onDelete={() => setMonth(null)} />}
      <PeriodCharts
        points={periods(rows, basis, 'month', range)}
        departurePoints={departureSeries(rows, basis)}
        basis={basis}
        tour={tour}
        tours={[]}
        showTourPicker={false}
        onTourChange={() => undefined}
        selectionLabel={null}
        unit="departure"
        onUnitChange={() => undefined}
        selected={month?.key || null}
        onSelect={setMonth}
        onDepartureSelect={() => undefined}
      />
      <SectionHeading
        title="Departure Level"
        subtitle={`Departures: ${visible.length}. Filter departures by action below.`}
        accent={tcc.predicted}
      />
      {!rows.some((d) => d.review_status) && (
        <>
          <Typography variant="subtitle2">Departure Action</Typography>
          <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap', my: 1 }}>
            <DepartureActionChip label="Departures: All" flag={null} selected={!flag} onClick={() => setFlag(null)} />
            {departureActionOrder.map((f) => (
              <DepartureActionChip
                key={f}
                label={`Departures: ${flagLabels[f]} (${scoped.filter((d) => basisValue(d, 'flag', basis) === f).length})`}
                flag={f}
                selected={flag === f}
                onClick={() => setFlag(flag === f ? null : f)}
              />
            ))}
          </Stack>
        </>
      )}
      <TableContainer component={Card} variant="outlined" sx={{ overflow: 'visible' }}>
        {/* Let the admin page scroll owner drive stickiness, rather than TableContainer's overflow. */}
        <Table
          stickyHeader
          size="small"
          aria-label="Departure drill-down table"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& > tbody > tr > td': {
              verticalAlign: 'top',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 12,
              px: 0.5,
              py: 1,
            },
            '& > thead > tr > th': {
              position: 'sticky',
              top: 0,
              zIndex: 3,
              whiteSpace: 'normal',
              overflowWrap: 'normal',
              wordBreak: 'normal',
              px: 0.5,
              fontSize: 12,
              bgcolor: tcc.booked,
              color: '#fff',
            },
            '@media (max-width: 767px)': {
              '&, & > tbody': { display: 'block' },
              '& > thead': { display: 'none' },
              '& > tbody > tr': {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& > tbody > tr > td': { display: 'block', minWidth: 0, borderBottom: 0, px: 1 },
              '& > tbody > tr > td::before': {
                content: 'attr(data-label)',
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                mb: 0.5,
              },
              '& > tbody > tr > td:first-of-type, & > tbody > tr > td:last-of-type': { gridColumn: '1 / -1' },
            },
          }}
        >
          <TableHead>
            <TableRow>
              {[
                'Departure Date',
                'Status',
                'Load Factor',
                'Predicted Load Factor',
                'Confidence',
                'Price',
                'Current Booked Numbers',
                'Predicted at Handback',
                'Departure Action',
                'Online Interactions: Last 28 Days',
              ].map((label, index) => (
                <TableCell key={label} sx={{ width: [10, 8, 6, 10, 9, 13, 11, 11, 8, 14][index] + '%' }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...visible]
              .sort((x, y) => x.dep.localeCompare(y.dep))
              .map((d) => (
                <React.Fragment key={d.dep_id}>
                  <TableRow>
                    <TableCell data-label="Departure Date">
                      {getDayJs(d.dep).format('D MMM YYYY')}
                      <Typography variant="caption" display="block" sx={{ whiteSpace: 'normal' }}>
                        Handback {getDayJs(d.handback).format('D MMM YYYY')}
                      </Typography>
                    </TableCell>
                    <TableCell data-label="Status">
                      <DepartureStatus departure={d} />
                      {d.margin_notes?.map((note) => (
                        <Typography key={note} variant="caption" display="block" color="text.secondary">
                          {note}
                        </Typography>
                      ))}
                      {bundleTravellerNote(d) !== null && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {bundleTravellerNote(d)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell data-label="Load Factor">
                      <Chip
                        size="small"
                        label={pct(d.lf)}
                        sx={{ bgcolor: alpha(tcc.booked, 0.1), color: tcc.bookedText }}
                      />
                      <Typography variant="caption" display="block">
                        {d.pax}{' '}
                        <Box component="span" data-seats-text sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                          of {d.max_pax} seats
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell data-label="Predicted Load Factor">
                      {emptyPredictionText(d) ? (
                        <EmptyPrediction departure={d} />
                      ) : (
                        <>
                          <Tooltip title={`P10–P90: ${count(d.pred_pax_p10)}–${count(d.pred_pax_p90)} travellers`}>
                            <Chip
                              size="small"
                              label={pct(d.pred_lf)}
                              sx={{ bgcolor: alpha(tcc.predicted, 0.15), color: tcc.predictedText }}
                            />
                          </Tooltip>
                          <Typography variant="caption" display="block">
                            {count(d.pred_pax)}{' '}
                            <Box component="span" data-seats-text sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                              of {d.max_pax} seats
                            </Box>
                          </Typography>
                        </>
                      )}
                      <Tooltip title={d[`breakeven_pax_${basis}`] == null ? breakEvenUnavailableTooltip : ''}>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          tabIndex={d[`breakeven_pax_${basis}`] == null ? 0 : undefined}
                          data-breakeven
                          sx={{ whiteSpace: 'normal' }}
                        >
                          {breakEvenText(d, basis)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell data-label="Confidence" sx={{ pr: 1.5 }}>
                      <ConfidenceBadge
                        final={d.days_to_handback <= 0}
                        tier={d.pred_confidence_tier}
                        capacity={d.max_pax}
                        source={d.pred_pax_source}
                        score={d.pred_confidence}
                      />
                    </TableCell>
                    <TableCell data-label="Price">
                      <DeparturePrice departure={d} />
                      <Typography variant="caption" display="block">
                        RM status: {rmStatus(d)}
                      </Typography>
                      <RecommendationPending departure={d} />
                      <TccPricingRecommendation departure={d} />
                    </TableCell>
                    <TableCell data-label="Current Booked Numbers" sx={{ bgcolor: alpha(tcc.booked, 0.06) }}>
                      <Box data-testid="departure-metric-value">TTV {money(d.ttv)}</Box>
                      <Tooltip title={costTooltip(d, basis, false)}>
                        <Typography variant="caption" display="block" tabIndex={0}>
                          Cost {money(agg([d], basis).cost)}
                        </Typography>
                      </Tooltip>
                      <Tooltip
                        title={
                          cancellationMoneyNote(d) ? `${cancellationMoneyNote(d)}. ${insuranceBreakageTooltip}` : ''
                        }
                      >
                        <Box tabIndex={cancellationMoneyNote(d) ? 0 : undefined}>
                          <Typography variant="caption" display="block" sx={{ whiteSpace: 'normal' }}>
                            Margin{' '}
                            {(d.ltle_margin_pax > 0 || d[`margin_bands_${basis}`]?.margin_available) &&
                            d[`margin_pct_${basis}`] != null
                              ? `${money(d[`le_margin_${basis}`])} · ${pct(d[`margin_pct_${basis}`])}`
                              : '—'}
                          </Typography>
                          {cancellationMoneyCaption(d) && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ fontSize: 10, lineHeight: 1.2 }}
                            >
                              {cancellationMoneyCaption(d)}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell data-label="Predicted at Handback" sx={{ bgcolor: alpha(tcc.predicted, 0.12) }}>
                      <Box data-testid="departure-metric-value">TTV {d.pred_ttv == null ? '—' : money(d.pred_ttv)}</Box>
                      <Tooltip title={costTooltip(d, basis, true)}>
                        <Typography variant="caption" display="block" tabIndex={0}>
                          Cost {money(agg([d], basis).pcost)}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" display="block" sx={{ whiteSpace: 'normal' }}>
                        Margin {d[`pred_margin_${basis}`] == null ? '—' : money(d[`pred_margin_${basis}`])} ·{' '}
                        {pct(d[`pred_margin_pct_${basis}`])}
                      </Typography>
                    </TableCell>
                    <TableCell data-label="Departure Action">
                      <ReviewStatusPill departure={d} basis={basis} today={snapshotDate ?? ''} />
                      <MarginBandChip departure={d} basis={basis} showConfidence={false} />
                    </TableCell>
                    <TableCell data-label="Online Interactions: Last 28 Days">
                      <Box
                        data-interaction-tiles
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                          gap: 0.5,
                          width: '100%',
                        }}
                      >
                        {[
                          ['Offer Page Views (Date)', d.booking_flow_views_28d],
                          ['Add to Cart', d.add_to_cart_28d],
                          ['Checkout Page Views', d.checkout_views_28d],
                        ].map(([label, value]) => (
                          <Box
                            key={String(label)}
                            sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 0.5, textAlign: 'center', minWidth: 0 }}
                          >
                            <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                              {count(value as number | null)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10}>
                      <ReviewPanel departure={d} basis={basis} />
                    </TableCell>
                  </TableRow>
                  {!!d.packages?.length && (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <DeparturePackages packages={d.packages} basis={basis} departure={d} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ReviewHelp />
      <SectionHeading title="Key" subtitle="Margin bands and departure actions." accent={tcc.predicted} />
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}
      >
        <Card variant="outlined" sx={{ minWidth: 0 }}>
          <Table
            size="small"
            aria-label="Margin band key"
            sx={{ tableLayout: 'fixed', width: '100%', '& th, & td': { px: 1, py: 0.75, overflowWrap: 'anywhere' } }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '42%', fontWeight: 700 }}>Margin band</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Predicted margin at handback</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keyMarginBands.map((band) => (
                <TableRow key={band}>
                  <TableCell>
                    <MarginBandChip legendBand={band} />
                  </TableCell>
                  <TableCell>{marginBandRanges[band]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 1 }}>
            Departures past handback use their final booked margin.
          </Typography>
        </Card>
        {!rows.some((d) => d.review_status) && (
          <Card variant="outlined" sx={{ minWidth: 0 }}>
            <Table
              size="small"
              aria-label="Departure action key"
              sx={{ tableLayout: 'fixed', width: '100%', '& th, & td': { px: 1, py: 0.75, overflowWrap: 'anywhere' } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '42%', fontWeight: 700 }}>Departure action</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>When it shows</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departureActionDefinitions.map(({ flag: definitionFlag, label, description }) => (
                  <TableRow key={definitionFlag}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={label}
                        sx={{
                          color: actionText(definitionFlag),
                          bgcolor: alpha(actionColour(definitionFlag), 0.14),
                          height: 'auto',
                          maxWidth: '100%',
                          '& .MuiChip-label': { whiteSpace: 'normal', px: 1, py: 0.5 },
                        }}
                      />
                    </TableCell>
                    <TableCell>{description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 1 }}>
              All actions are per departure.
            </Typography>
          </Card>
        )}
      </Box>
      <Button onClick={back} variant="outlined" size="large" sx={{ borderRadius: 10, px: 3, mt: 3 }}>
        ← Back to All Tours
      </Button>
    </Container>
  );
}
